import { useContext, useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { DataView } from "primereact/dataview";
import { WindowSizeContext } from "../../Context/WindowSizeContext";
import TextField from "../Textfield/TextField";

const ResponsiveDataList = ({
  data,
  columns,
  filters,
  onFilter,
  itemTemplate,
  dataViewHeader,
  paginator = true,
  rows = 10,
  rowsPerPageOptions = undefined,
  pageNumber,
  totalCount,
  className = "",
  dataKey = "id",
  stripedRows = true,
  size = "large",
  rowHover = true,
  selection,
  onSelectionChange,
  enableSelection = false,
  selectOnCardClick = false,
  onPageChange,
  typeFilter = false,
  selectionMode = "multiple",
  onRowClick,
}) => {
  const { isMobile } = useContext(WindowSizeContext);
  const [localFilter, setLocalFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchType, setSearchType] = useState(null);

  const globalFilter = localFilter;

  const onGlobalFilterChange = (e) => {
    setSearchInput(e.target.value);
  };

  const filteredData = useMemo(() => {
    if (!data) return [];
    if (!searchInput) return data;
    return data.filter((item) =>
      Object.values(item).some((val) =>
        String(val).toLowerCase().includes(searchInput.toLowerCase())
      )
    );
  }, [data, searchInput]);

  const handlePage = (e) => {
    const newPage = e.rows > 0 ? e.first / e.rows + 1 : 1;
    onPageChange?.({ pageNumber: newPage, pageSize: e.rows });
  };

  const wrappedItemTemplate = (item) => {
    const isSelected =
      selectionMode === "single"
        ? selection && selection[dataKey] === item[dataKey]
        : Array.isArray(selection) &&
          selection.some((u) => u[dataKey] === item[dataKey]);

    const cardContent = (
      <div
        className={`card p-3 mb-2 ${isSelected ? "bg-success-subtle" : ""}`}
        style={{
          cursor: onRowClick || selectOnCardClick ? "pointer" : "default",
          border: isSelected ? "2px solid #10b981" : "1px solid #ccc",
          transition: "all 0.2s ease-in-out",
        }}
        title={onRowClick ? "برای مشاهده جزئیات کلیک کنید" : undefined}
      >
        {itemTemplate(item)}
      </div>
    );

    if (onRowClick) {
      return <div onClick={() => onRowClick(item)}>{cardContent}</div>;
    }

    if (!selectOnCardClick) return cardContent;

    const handleClick = () => {
      if (!onSelectionChange) return;

      if (selectionMode === "single") {
        const alreadySelected =
          selection && selection[dataKey] === item[dataKey];

        onSelectionChange({
          value: alreadySelected ? null : item,
        });
      } else {
        const alreadySelected =
          Array.isArray(selection) &&
          selection.some((u) => u[dataKey] === item[dataKey]);
        const newSelection = alreadySelected
          ? selection.filter((u) => u[dataKey] !== item[dataKey])
          : [...(selection || []), item];

        onSelectionChange({ value: newSelection });
      }
    };

    return <div onClick={handleClick}>{cardContent}</div>;
  };

  let mobileHeader = null;

  if (typeFilter === false) {
    mobileHeader = (
      <div className="d-flex flex-column gap-2 mb-2">
        <TextField
          className="form-control"
          type="text"
          value={searchInput}
          onChange={onGlobalFilterChange}
          label="جستجو..."
          disabled={searchType == null}
        />
      </div>
    );
  }

  if (isMobile) {
    return (
      <DataView
        value={filteredData}
        itemTemplate={wrappedItemTemplate}
        header={mobileHeader}
        paginator={paginator}
        rows={rows}
        rowsPerPageOptions={rowsPerPageOptions}
        className="compact-paginator farsi-monospace-digit"
        lazy={!!totalCount}
        totalRecords={totalCount}
        first={((pageNumber ?? 1) - 1) * rows}
        onPage={handlePage}
      />
    );
  }

  return (
    <>
      {dataViewHeader}
      <div>
        <DataTable
          value={filteredData}
          dataKey={dataKey}
          paginator={paginator}
          rows={rows}
          rowsPerPageOptions={rowsPerPageOptions}
          stripedRows={stripedRows}
          size={size}
          rowHover={rowHover}
          filterDisplay="row"
          filters={filters}
          onFilter={onFilter}
          globalFilter={globalFilter}
          globalFilterFields={columns.map((col) => col.field).filter(Boolean)}
          className={`${className} rtl-datatable`}
          selection={enableSelection ? selection : null}
          onSelectionChange={enableSelection ? onSelectionChange : undefined}
          lazy={!!totalCount}
          totalRecords={totalCount}
          first={((pageNumber ?? 1) - 1) * rows}
          onPage={handlePage}
          resizableColumns
          columnResizeMode="fit"
          showGridlines
          onRowClick={onRowClick ? (e) => onRowClick(e.data) : undefined}
          rowClassName={() => (onRowClick ? "clickable-row" : "")}
        >
          {enableSelection && (
            <Column
              selectionMode={selectionMode}
              headerStyle={{ width: "3rem" }}
            />
          )}
          {columns.map((col, index) => (
            <Column key={index} {...col} />
          ))}
        </DataTable>

        {/* استایل ردیف کلیک‌پذیر بدون نیاز به فایل css جدا */}
        {onRowClick && (
          <style>
            {`
              .clickable-row { cursor: pointer; }
              .rtl-datatable .p-datatable-tbody > tr.clickable-row:hover {
                filter: brightness(0.98);
              }
              @keyframes historyInstallPop {
                from { transform: translateY(8px) scale(0.98); opacity: 0; }
                to   { transform: translateY(0) scale(1); opacity: 1; }
              }
            `}
          </style>
        )}
      </div>
    </>
  );
};

export default ResponsiveDataList;
